import type { ReactNode } from "react";

import { BrandLogo } from "@/components/shared/BrandLogo";
import { LanguageSwitcher } from "@/components/shared/LanguageSwitcher";
import { cn } from "@/lib/utils";

export interface AuthCover {
  title: string;
  body: string;
  /**
   * The quiet line under the body. A node rather than a string because some
   * notes offer a way out — "¿Problemas para entrar? Escribinos." has to
   * actually reach support, and LiveChat is not loaded on the auth paths.
   */
  note?: ReactNode;
}

/**
 * A link inside the cover note. Styled for the navy half, where the note sits
 * at `white/50` and the link has to read as clickable without shouting.
 */
export function AuthCoverLink({
  href,
  children,
}: {
  href?: string;
  children?: ReactNode;
}): JSX.Element {
  return (
    <a
      href={href}
      className="font-medium text-white/80 underline underline-offset-4 transition-colors hover:text-white"
    >
      {children}
    </a>
  );
}

/**
 * The signed-out shell: a full-bleed 50/50 with the brand navy as the COVER
 * half (decision ab0acd1).
 *
 * Extracted rather than copied. TEN pages under pages/auth render on
 * `bg-auth-surface` — login, register, forgot/reset password, verify, name
 * entry, complete profile, the callbacks — and hand-rolling this grid in each
 * is how they drift apart one fix at a time. The grid, the logo placement, the
 * language switcher, the terms line and the lg breakpoint live here once.
 *
 * `cover` is optional: pages with nothing to say opposite the form (a spinner,
 * a callback) pass none and get a plain centred column, which is the right
 * answer for them rather than an empty navy half.
 */
export function AuthShell({
  children,
  cover,
  footer,
  width = "md",
  coverSide = "right",
}: {
  children: ReactNode;
  cover?: AuthCover | null;
  /** Usually the terms line. Rendered small and centred under the form. */
  footer?: ReactNode;
  /**
   * How wide the form column may grow.
   *
   * `md` is the default and is right for everything this shell was extracted
   * from — a login form is a handful of stacked fields and reads worse wide.
   * `xl` exists for the INVITATION wizards, which carry a three-column
   * department grid and a file upload inside sectioned cards; at `md` that
   * grid wraps to two columns and the sections lose the breathing room the
   * rest of the page is built at.
   *
   * A named pair rather than a className, so a page cannot quietly invent a
   * third width and start the drift this component exists to prevent.
   */
  width?: "md" | "xl";
  /**
   * Which half the navy cover takes on a wide screen.
   *
   * `right` is the default and is what the ten pages extracted into this
   * shell have always rendered. `left` exists for the INVITATION flow, where
   * the form is the long, working half and reads better against the edge the
   * eye returns to.
   *
   * SOURCE ORDER DOES NOT CHANGE — only `order`. The form stays first in the
   * DOM, so a keyboard, a screen reader and every screen below `lg` (where
   * the cover is hidden entirely) still meet the form first.
   */
  coverSide?: "left" | "right";
}): JSX.Element {
  return (
    /*
      THE COLUMN SCROLLS, NOT THE DOCUMENT. `html` carries overflow hidden —
      this app scrolls inside containers — so a column with `overflow-y-auto`
      and no height CAP simply grows past the viewport and clips: nothing
      scrolls, in either direction. That is latent for every short page on
      this shell and bites the first long one (the invitation wizards, ~2130px
      against a 900px viewport), which is exactly how it shipped.

      `h-svh` is therefore load-bearing, not cosmetic. The hand-rolled wrapper
      these pages used before carried `max-h-svh` for the same reason.
    */
    <div className={cover ? "grid h-svh lg:grid-cols-2" : "h-svh"}>
      <div
        className={cn(
          "flex h-svh flex-col gap-4 overflow-y-auto p-6 md:p-10 lg:h-full",
          // THE FORM GOES SECOND on a wide screen when the cover takes the
          // left: source order stays form-then-cover so a screen reader and a
          // phone meet the form first, and only the painted order changes.
          coverSide === "left" ? "lg:order-2" : null,
        )}
      >
        <div className="flex items-center justify-between gap-2">
          <BrandLogo variant="full" imgClassName="h-7 w-auto" />
          <LanguageSwitcher />
        </div>
        <div className="flex flex-1 items-center justify-center">
          <div className={width === "xl" ? "w-full max-w-xl" : "w-full max-w-md"}>
            {children}
          </div>
        </div>
        {footer ? (
          <div className="text-center text-xs text-muted-foreground">{footer}</div>
        ) : null}
      </div>

      {/* Hidden below lg: on a phone a decorative half would push the form
          off-screen, so it collapses to a single column rather than shrinking
          to a useless strip. */}
      {cover ? (
        <div
          className={cn(
            "relative hidden bg-auth-surface lg:flex lg:h-svh lg:flex-col lg:justify-center lg:px-14 xl:px-20",
            coverSide === "left" ? "lg:order-1" : null,
          )}
        >
          {/* Measure capped in CHARACTERS, not by the column: at half of a wide
              monitor a rem-based max-width still broke the headline mid-phrase. */}
          <div
            key={cover.title}
            className="min-h-[13rem] max-w-[34ch] animate-in fade-in duration-150"
          >
            <p className="text-3xl font-semibold leading-[1.15] tracking-tight text-white">
              {cover.title}
            </p>
            <p className="mt-5 text-base leading-relaxed text-white/70">{cover.body}</p>
            {cover.note ? (
              <p className="mt-10 text-sm text-white/50">{cover.note}</p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
